import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Icons } from '@beeline/design-tokens/js/iconfont';

import clsx from 'clsx';
import { CarPlace } from 'src/components/ParkingPlace/CarPlace';
import { ParkingPlace } from 'src/components/ParkingPlace/ParkingPlace';
import { getCurrentParkingPlace } from 'src/store/parking/selectors';
import {
  getEmployeeNumber,
  getUserPermissions,
} from 'src/store/user/selectors';
import { Parking } from 'src/types';

import classes from './BookingParkingPlace.module.scss';

export interface BookingParkingPlaceProps {
  item: Parking;
}

export const BookingParkingPlace: FC<BookingParkingPlaceProps> = ({ item }) => {
  const data = useSelector(getUserPermissions);
  const parking = useSelector(getCurrentParkingPlace);
  const employeeNumber = useSelector(getEmployeeNumber);

  let bookedByMe = false;
  let bookedByCollegue = false;
  const isAdmin = !!data?.adminSpaces.size;
  const employeeSpaces = data?.spaces ?? new Set();

  if (item.status) {
    if (item.status.responsibleEmployeeNumber === employeeNumber) {
      bookedByMe = true;
    } else {
      bookedByCollegue = true;
    }
  }
  const booked = bookedByMe || bookedByCollegue;
  const isReservedByEmployee =
    !!item.responsibleEmployeeNumber &&
    item.responsibleEmployeeNumber !== employeeNumber &&
    (isAdmin || !item.shared);

  const isReservedBySpace =
    !!item.spaceId && !employeeSpaces.has(item.spaceId) && !isAdmin;
  const isGuest = item.guest && !isAdmin;

  const active = parking?.mapPlaceId === item.mapPlaceId;

  const inactive =
    bookedByCollegue ||
    (isReservedByEmployee && !item.shared) ||
    isReservedBySpace;

  const hoverTitle = useMemo((): string | undefined => {
    if (bookedByCollegue) return 'Забронировано';

    if (isReservedByEmployee) return 'Закреплено за сотрудником';

    if (isReservedBySpace) return 'Место не относится к твоему блоку';

    if (isGuest) return 'Место может быть забронировано только администратором';
  }, [bookedByCollegue, isGuest, isReservedByEmployee, isReservedBySpace]);

  const showCar =
    booked || isReservedByEmployee || isReservedBySpace || isGuest;
  const unavailable =
    bookedByCollegue || isReservedByEmployee || isReservedBySpace || isGuest;

  const icon = isReservedByEmployee && item.shared ? Icons.Clock : undefined;

  if (showCar)
    return (
      <CarPlace
        item={item}
        containerClassName={classes.carContainer}
        carClassName={clsx(
          classes.car,
          bookedByMe && classes.bookedByMe,
          unavailable && classes.unavailable,
          active && classes.activeCar,
        )}
        contentClassName={classes.title}
        title={hoverTitle}
        icon={icon}
      />
    );

  return (
    <ParkingPlace
      item={item}
      className={clsx(
        classes.container,
        bookedByMe && classes.bookedByMe,
        inactive && classes.inactive,
        active && classes.active,
      )}
      contentClassName={classes.title}
      title={hoverTitle}
    />
  );
};
